import { useMemo } from 'react';
import { ImageStyle, StyleProp } from 'react-native';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBUserImage } from '@shared/components/image';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import styles from './SeenByList.module.css';

export interface SeenByListProps {
  seen: API.ShortUserFragment[];
  notSeen: API.ShortUserFragment[];
  keyword: string;
  hasMoreMembers?: boolean;
}

const userImageStyle: StyleProp<ImageStyle> = {
  width: 24,
  height: 24,
  borderRadius: 12,
  overflow: 'hidden',
};

export function SeenByList({ seen, notSeen, keyword, hasMoreMembers = false }: SeenByListProps) {
  const seenList = useMemo(() => {
    return seen.filter(user => user.fullName.toLowerCase().includes(keyword.toLowerCase()));
  }, [seen, keyword]);

  const notSeenList = useMemo(() => {
    return notSeen.filter(user => user.fullName.toLowerCase().includes(keyword.toLowerCase()));
  }, [notSeen, keyword]);

  const seenHeader = hasMoreMembers
    ? i18n('chat.seenBySheet.seenHeader')
    : i18n('chat.seenBySheet.seen', { count: seenList.length });

  const notSeenHeader = hasMoreMembers
    ? i18n('chat.seenBySheet.notSeenHeader')
    : i18n('chat.seenBySheet.notSeen', { count: notSeenList.length });

  return (
    <Menu.Scrollable showScrollIndicators>
      <Menu.Section gap={1}>
        <Text size="overline" weight="semibold" className={styles.menuSectionTitle}>
          {seenHeader}
        </Text>
        {seenList.map(user => (
          <SeenByUser key={user.id} user={user} />
        ))}
      </Menu.Section>
      <Menu.Separator />
      <Menu.Section gap={1}>
        <Text size="overline" weight="semibold" className={styles.menuSectionTitle}>
          {notSeenHeader}
        </Text>
        {notSeenList.map(user => (
          <SeenByUser key={user.id} user={user} />
        ))}
      </Menu.Section>
    </Menu.Scrollable>
  );
}

function SeenByUser({ user }: { user: API.ShortUserFragment }) {
  return (
    <View row centerVertically gap={1}>
      <MBUserImage size="small" user={user} style={userImageStyle} />
      <Text flex size="body2" numberOfLines={2}>
        {user.fullName}
      </Text>
    </View>
  );
}
